import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import { correspond, lireCarnet, type Carnet, type ContactCrm } from '../lib/crm'

/**
 * Contacts au chantier : surintendant, chargé de projet de l'entrepreneur
 * général, architecte, etc. Tout le monde assigné au chantier les voit ;
 * sur téléphone, un toucher sur le numéro lance l'appel et un toucher sur le
 * courriel ouvre un nouveau message. Seuls les administrateurs les modifient.
 *
 * Module Ventes (4 nov. 2026) : un contact se choisit dans le carnet ; un
 * contact tapé à la main est ajouté au carnet en enregistrant (ou relié à la
 * fiche qui a le même courriel). La ligne du chantier garde une copie du nom,
 * du téléphone et du courriel, pour que les chefs la voient sans accès au
 * carnet ; modifier la fiche du carnet met cette copie à jour (déclencheur).
 */
export interface Contact {
  id: string
  chantier_id: string
  nom: string
  titre: string | null
  telephone: string | null
  courriel: string | null
  ordre: number
  contact_id: string | null
}

type Brouillon = { nom: string; titre: string; telephone: string; courriel: string; contact_id: string | null; compagnie_id: string }
const VIDE: Brouillon = { nom: '', titre: '', telephone: '', courriel: '', contact_id: null, compagnie_id: '' }

export function ContactsChantier({
  chantierId,
  modifiable
}: {
  chantierId: string
  modifiable: boolean
}) {
  const [contacts, setContacts] = useState<Contact[] | null>(null)
  const [edition, setEdition] = useState<string | 'nouveau' | null>(null)
  const [brouillon, setBrouillon] = useState<Brouillon>(VIDE)
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)
  const [carnet, setCarnet] = useState<Carnet | null>(null)
  const [cherche, setCherche] = useState('')

  const charger = useCallback(async () => {
    const { data, error } = await supabase
      .from('chantier_contacts')
      .select('*')
      .eq('chantier_id', chantierId)
      .order('ordre')
      .order('cree_le')
    if (error) setErreur(error.message)
    else setContacts((data as Contact[]) ?? [])
  }, [chantierId])

  useEffect(() => {
    void charger()
  }, [charger])

  function ouvrir(c: Contact | null) {
    setErreur(null)
    setCherche('')
    // Le carnet n'est lu que par un admin, au moment d'ajouter ou de modifier.
    if (!carnet) void lireCarnet().then(setCarnet).catch(() => setCarnet(null))
    if (c) {
      setEdition(c.id)
      setBrouillon({
        nom: c.nom,
        titre: c.titre ?? '',
        telephone: c.telephone ?? '',
        courriel: c.courriel ?? '',
        contact_id: c.contact_id,
        compagnie_id: ''
      })
    } else {
      setEdition('nouveau')
      setBrouillon(VIDE)
    }
  }

  const nomCompagnie = useMemo(() => new Map((carnet?.compagnies ?? []).map((c) => [c.id, c.nom])), [carnet])
  const choix = useMemo(() => {
    if (!carnet || cherche.trim().length < 2) return []
    return carnet.contacts
      .filter((c) => correspond(cherche, c.nom, c.courriel, c.fonction, c.compagnie_id ? nomCompagnie.get(c.compagnie_id) : null))
      .slice(0, 8)
  }, [carnet, cherche, nomCompagnie])

  function choisir(c: ContactCrm) {
    setBrouillon({
      nom: c.nom,
      titre: brouillon.titre || c.fonction || '',
      telephone: c.cellulaire || c.telephone || '',
      courriel: c.courriel ?? '',
      contact_id: c.id,
      compagnie_id: c.compagnie_id ?? ''
    })
    setCherche('')
  }

  /** Fiche du carnet pour ce contact : celle choisie, celle du même courriel, ou une nouvelle. */
  async function ficheCarnet(v: { nom: string; titre: string | null; telephone: string | null; courriel: string | null }): Promise<string | null> {
    if (brouillon.contact_id) {
      const fiche = carnet?.contacts.find((c) => c.id === brouillon.contact_id)
      const tel = fiche?.cellulaire ? { cellulaire: v.telephone } : { telephone: v.telephone }
      const { error } = await supabase
        .from('contacts')
        .update({ nom: v.nom, courriel: v.courriel?.toLowerCase() ?? null, ...tel })
        .eq('id', brouillon.contact_id)
      if (error) throw new Error(error.message)
      return brouillon.contact_id
    }
    if (v.courriel) {
      const meme = carnet?.contacts.find((c) => c.courriel?.toLowerCase() === v.courriel!.toLowerCase())
      if (meme) return meme.id
    }
    const r = await supabase
      .from('contacts')
      .insert({
        nom: v.nom,
        fonction: v.titre,
        telephone: v.telephone,
        courriel: v.courriel?.toLowerCase() ?? null,
        compagnie_id: brouillon.compagnie_id || null
      })
      .select('id')
      .single()
    if (r.error) throw new Error(r.error.message)
    return r.data.id as string
  }

  async function enregistrer(e: React.FormEvent) {
    e.preventDefault()
    if (!brouillon.nom.trim()) {
      setErreur('Le nom est obligatoire.')
      return
    }
    setOccupe(true)
    setErreur(null)
    const vide = (v: string) => v.trim() || null
    const base = {
      nom: brouillon.nom.trim(),
      titre: vide(brouillon.titre),
      telephone: vide(brouillon.telephone),
      courriel: vide(brouillon.courriel)
    }
    let contact_id: string | null = brouillon.contact_id
    try {
      contact_id = await ficheCarnet(base)
    } catch (err) {
      setOccupe(false)
      setErreur(`Carnet de contacts : ${(err as Error).message}`)
      return
    }
    const valeurs = { ...base, contact_id }
    const { error } =
      edition === 'nouveau'
        ? await supabase
            .from('chantier_contacts')
            .insert({ ...valeurs, chantier_id: chantierId, ordre: contacts?.length ?? 0 })
        : await supabase.from('chantier_contacts').update(valeurs).eq('id', edition!)
    setOccupe(false)
    if (error) {
      setErreur(error.message)
      return
    }
    setEdition(null)
    await charger()
  }

  async function supprimer(c: Contact) {
    if (!window.confirm(`Retirer ${c.nom} des contacts du chantier ?`)) return
    setOccupe(true)
    const { error } = await supabase.from('chantier_contacts').delete().eq('id', c.id)
    setOccupe(false)
    if (error) setErreur(error.message)
    setEdition(null)
    await charger()
  }

  const formulaire = (c: Contact | null) => (
    <form className="carte bande-nulle" onSubmit={(e) => void enregistrer(e)}>
      {!brouillon.contact_id && (
        <>
          <label className="champ">
            <span>Chercher dans le carnet</span>
            <input
              type="search"
              value={cherche}
              onChange={(e) => setCherche(e.target.value)}
              placeholder={carnet ? 'Nom, compagnie ou courriel…' : 'Chargement du carnet…'}
              autoFocus={!c}
            />
          </label>
          {choix.length > 0 && (
            <div className="jetons" style={{ marginTop: -6, marginBottom: 12 }}>
              {choix.map((x) => (
                <button type="button" key={x.id} className="jeton" onClick={() => choisir(x)}>
                  {x.nom}
                  {x.compagnie_id ? ` · ${nomCompagnie.get(x.compagnie_id)}` : ''}
                </button>
              ))}
            </div>
          )}
          {cherche.trim().length >= 2 && carnet && choix.length === 0 && (
            <p className="code" style={{ marginTop: -6, marginBottom: 12 }}>
              Pas dans le carnet : remplis la fiche ci-dessous, le contact y sera ajouté.
            </p>
          )}
        </>
      )}
      {brouillon.contact_id && (
        <p className="code" style={{ marginBottom: 10 }}>
          Relié au carnet
          {(() => {
            const k = carnet?.contacts.find((x) => x.id === brouillon.contact_id)
            return k?.compagnie_id ? ` · ${nomCompagnie.get(k.compagnie_id)}` : ''
          })()}
          {!c?.contact_id && (
            <>
              {' · '}
              <button type="button" className="lien-contact" style={{ padding: 0 }} onClick={() => setBrouillon({ ...VIDE, titre: brouillon.titre })}>
                changer
              </button>
            </>
          )}
        </p>
      )}
      <label className="champ">
        <span>Nom *</span>
        <input
          value={brouillon.nom}
          onChange={(e) => setBrouillon({ ...brouillon, nom: e.target.value })}
        />
      </label>
      {!brouillon.contact_id && carnet && (
        <label className="champ">
          <span>Compagnie (pour le carnet)</span>
          <select value={brouillon.compagnie_id} onChange={(e) => setBrouillon({ ...brouillon, compagnie_id: e.target.value })}>
            <option value="">— Aucune —</option>
            {carnet.compagnies.map((x) => (
              <option key={x.id} value={x.id}>
                {x.nom}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="champ">
        <span>Titre</span>
        <input
          value={brouillon.titre}
          onChange={(e) => setBrouillon({ ...brouillon, titre: e.target.value })}
          placeholder="Ex. : surintendant, chargé de projet (EG), architecte"
        />
      </label>
      <p className="code" style={{ marginTop: -6, marginBottom: 12 }}>Titre propre à ce chantier.</p>
      <label className="champ">
        <span>Téléphone</span>
        <input
          type="tel"
          inputMode="tel"
          value={brouillon.telephone}
          onChange={(e) => setBrouillon({ ...brouillon, telephone: e.target.value })}
          placeholder="514 555-1234"
        />
      </label>
      <label className="champ">
        <span>Courriel</span>
        <input
          type="email"
          inputMode="email"
          value={brouillon.courriel}
          onChange={(e) => setBrouillon({ ...brouillon, courriel: e.target.value })}
        />
      </label>
      {erreur && (
        <p className="avis erreur" style={{ marginBottom: 12 }}>
          {erreur}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="bouton creux" disabled={occupe} onClick={() => setEdition(null)}>
          Annuler
        </button>
        <button className="bouton" disabled={occupe}>
          {occupe ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
      {c && (
        <button
          type="button"
          className="retour"
          style={{ marginTop: 12, textDecoration: 'underline' }}
          disabled={occupe}
          onClick={() => void supprimer(c)}
        >
          Retirer ce contact
        </button>
      )}
    </form>
  )

  return (
    <>
      <div className="sect">
        <span>
          Contacts au chantier <span className="code">{contacts?.length ?? ''}</span>
        </span>
        {modifiable && edition === null && (
          <button className="retour" onClick={() => ouvrir(null)}>
            + Ajouter
          </button>
        )}
      </div>

      <div className="liste">
        {contacts?.map((c) =>
          edition === c.id ? (
            <div key={c.id}>{formulaire(c)}</div>
          ) : (
            <div key={c.id} className="carte bande-nulle">
              <div className="entre">
                <div style={{ minWidth: 0 }}>
                  <p className="titre">{c.nom}</p>
                  {c.titre && (
                    <p className="meta">
                      <span>{c.titre}</span>
                    </p>
                  )}
                  <p className="meta" style={{ gap: 14 }}>
                    {c.telephone && (
                      <a className="lien-contact" href={`tel:${c.telephone.replace(/[^\d+]/g, '')}`}>
                        ☎ {c.telephone}
                      </a>
                    )}
                    {c.courriel && (
                      <a className="lien-contact" href={`mailto:${c.courriel}`}>
                        ✉ {c.courriel}
                      </a>
                    )}
                  </p>
                </div>
                {modifiable && edition === null && (
                  <button className="pastille p-ouvert" onClick={() => ouvrir(c)}>
                    Modifier
                  </button>
                )}
              </div>
            </div>
          )
        )}
        {edition === 'nouveau' && formulaire(null)}
        {contacts !== null && contacts.length === 0 && edition === null && (
          <p className="vide">
            Aucun contact pour ce chantier.
            {modifiable ? ' Ajoute le surintendant, le chargé de projet de l’EG, etc.' : ''}
          </p>
        )}
        {erreur && edition === null && <p className="avis erreur">{erreur}</p>}
      </div>
    </>
  )
}
